import { useState, useCallback, useRef } from 'react';

interface UseRecaptchaOptions {
  onVerify?: (token: string) => void;
  onError?: (error: string) => void;
  onExpired?: () => void;
  autoReset?: boolean;
}

interface UseRecaptchaReturn {
  token: string | null;
  isVerified: boolean;
  error: string | null;
  isLoading: boolean;
  handleVerify: (token: string) => void;
  handleError: (error: string) => void;
  handleExpired: () => void;
  reset: () => void;
  clearError: () => void;
}

export const useRecaptcha = (options: UseRecaptchaOptions = {}): UseRecaptchaReturn => {
  const { onVerify, onError, onExpired, autoReset = true } = options;
  
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const isVerified = Boolean(token && !error);

  const handleVerify = useCallback((newToken: string) => {
    setToken(newToken);
    setError(null);
    setIsLoading(false);
    console.log('reCAPTCHA token received:', newToken.substring(0, 20) + '...');
    onVerify?.(newToken);
  }, [onVerify]);

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    setToken(null);
    setIsLoading(false);
    onError?.(errorMessage);
  }, [onError]);

  const handleExpired = useCallback(() => {
    setToken(null);
    if (autoReset) {
      setError(null);
    } else {
      setError('reCAPTCHA has expired. Please verify again.');
    }
    setIsLoading(false);
    onExpired?.();
  }, [onExpired, autoReset]);

  const reset = useCallback(() => {
    setToken(null);
    setError(null);
    setIsLoading(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    token,
    isVerified,
    error,
    isLoading,
    handleVerify,
    handleError,
    handleExpired,
    reset,
    clearError,
  };
};

export default useRecaptcha;